import { useCallback, useEffect, useRef } from 'react';
import {
	InputAction,
	InputComboAction,
	KeyPressStates,
	MouseButton,
} from '~/shared';
import { rage } from '..';

/**
 * Хук для реагирования на двойной клик мыши
 */
export const useMouseDoubleClick = (
	button: MouseButton,
	callback: (e: MouseEvent) => void,
	doubleClickThreshold = 400,
) => {
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	const lastClickTimeRef = useRef(0);

	useEffect(() => {
		const mouseUpHandler = (e: MouseEvent) => {
			if (e.button !== button) {
				return;
			}

			const currentTime = Date.now();
			if (currentTime - lastClickTimeRef.current <= doubleClickThreshold) {
				callbackRef.current(e);
			}

			lastClickTimeRef.current = currentTime;
		};

		window.addEventListener('mouseup', mouseUpHandler);

		return () => {
			window.removeEventListener('mouseup', mouseUpHandler);
		};
	}, [button, doubleClickThreshold]);
};

/**
 * Хук для реагирования на кнопки мыши
 */
interface useMouseButtonOptions {
	state?: KeyPressStates;
}

export function useMouseButton(
	button: MouseButton,
	callback: (state: KeyPressStates) => void,
	options: useMouseButtonOptions = {},
): void {
	const { state = KeyPressStates.Pressed } = options;

	const handleMouseButton = useCallback(
		(eventButton: MouseButton, eventState: KeyPressStates) => {
			if (eventButton !== button) {
				return;
			}

			if (state !== KeyPressStates.Both && state !== eventState) {
				return;
			}

			callback(eventState);
		},
		[button, callback],
	);

	const handleMouseDown = useCallback(
		(e: MouseEvent) => handleMouseButton(e.button, KeyPressStates.Pressed),
		[handleMouseButton],
	);

	const handleMouseUp = useCallback(
		(e: MouseEvent) => handleMouseButton(e.button, KeyPressStates.Unpressed),
		[handleMouseButton],
	);

	useEffect(() => {
		window.addEventListener('mousedown', handleMouseDown);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousedown', handleMouseDown);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [button, callback]);
}

/**
 * Хук для реагирования на скролл
 */
export const useScroll = (callback: (direction: 'up' | 'down') => void) => {
	useEffect(() => {
		const handleWheel = (event: WheelEvent) => {
			const direction: 'up' | 'down' = event.deltaY > 0 ? 'down' : 'up';
			callback(direction);
		};

		window.addEventListener('wheel', handleWheel, { passive: true });

		return () => {
			window.removeEventListener('wheel', handleWheel);
		};
	}, [callback]);
};

/**
 * Хук для реагирования на нажатие клавиши
 */
interface UseInputActionOptions {
	state?: KeyPressStates;
	allowWhileFocused?: boolean;
}

export function useInputAction(
	action: InputAction,
	callback: (state: KeyPressStates) => void,
	options: UseInputActionOptions = {},
): void {
	const { state = KeyPressStates.Pressed, allowWhileFocused = false } = options;

	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	const actionLoadedRef = useRef<boolean | null>(null);

	useEffect(() => {
		const observable = rage.input.getActionState(action);

		if (actionLoadedRef.current === null) {
			actionLoadedRef.current = observable.getValue();
		}

		const sub = observable.subscribe((keyState) => {
			if (keyState === actionLoadedRef.current) {
				return;
			}

			if (
				(state === KeyPressStates.Pressed && keyState) ||
				(state === KeyPressStates.Unpressed && !keyState) ||
				state === KeyPressStates.Both
			) {
				const isInputFocused = rage.input.getIsAnyInputFocused().getValue();

				if (isInputFocused && !allowWhileFocused) {
					return;
				}

				callbackRef.current(
					keyState ? KeyPressStates.Pressed : KeyPressStates.Unpressed,
				);
			}

			actionLoadedRef.current = keyState;
		});

		return () => {
			sub.unsubscribe();
		};
	}, [action, state, allowWhileFocused]);
}

/**
 * Хук для реагирования на нажатие комбинации клавиши
 */
export function useInputComboAction(
	action: InputComboAction,
	callback: () => void,
): void {
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	const actionLoadedRef = useRef<boolean | null>(null);

	useEffect(() => {
		const observable = rage.input.getComboActionState(action);

		if (actionLoadedRef.current === null) {
			actionLoadedRef.current = observable.getValue();
		}

		const sub = observable.subscribe((keysState) => {
			if (keysState === actionLoadedRef.current) {
				return;
			}

			if (keysState) {
				const isInputFocused = rage.input.getIsAnyInputFocused().getValue();

				if (isInputFocused) {
					return;
				}

				callbackRef.current();
			}

			actionLoadedRef.current = keysState;
		});

		return () => {
			sub.unsubscribe();
		};
	}, [action]);
}
